import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Header from "../../component/Header";
import Pagination from "../../component/Pagination";
import DiamondsFour from "../../component/SvgIcons/DiamondsFour";
import IconDate from "../../component/SvgIcons/IconDate";
import IconDateActive from "../../component/SvgIcons/IconDateActive";
import IconDots from "../../component/SvgIcons/IconDotes";
import IconEdit from "../../component/SvgIcons/IconEdit";
import IconFilter from "../../component/SvgIcons/IconFilter";
import IconPlus from "../../component/SvgIcons/IconPlus";
import IconSearchTable from "../../component/SvgIcons/IconSearchTable";
import IconView from "../../component/SvgIcons/IconView";
import { motion, AnimatePresence } from "framer-motion";
import api from "../../services/axios";
import "./style.css";
import numberOptions from "../../component/NumberOptions/numberOptionsdata";
import CustomDropdown from "../../component/CustomDropdown";
import Avatar from "../../component/NumberOptions/Avatar";
import { useSwal } from "../../services/SweetAlert";
import utils from "../../utils/utils";

export default function ManageAgents() {
  const [selectedRows, setSelectedRows] = useState([]);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortOrder, setSortOrder] = useState("asc");
  const [sortBy, setSortBy] = useState("Agent_Name");
  const currentUser = JSON.parse(localStorage.getItem("user")); // Adjust key as needed

  const [currentPage, setCurrentPage] = useState(1);
  const dropdownRefs = useRef({});
  const [itemsPerPage, setItemsPerPage] = useState(
    window.innerWidth >= 1920 ? 16 : 10
  );
  const IMAGE_BASE_URL = process.env.REACT_APP_IMAGE_URL;
  const { alert, confirm } = useSwal();
  const selectAllRef = useRef();
  const formatDate = (dateString) => {
    const d = new Date(dateString);
    const pad = (n) => n.toString().padStart(2, "0");

    let hours = d.getHours();
    const minutes = pad(d.getMinutes());
    const ampm = hours >= 12 ? "PM" : "AM";

    hours = hours % 12 || 12; // Convert 0 to 12 for 12 AM/PM
    const formattedTime = `${pad(hours)}:${minutes} ${ampm}`;
    const formattedDate = `${pad(d.getDate())}-${pad(
      d.getMonth() + 1
    )}-${d.getFullYear()}`;

    return `${formattedDate} ${formattedTime}`;
  };

  useEffect(() => {
    utils.saveRecentSearch("Manage Agent Details", "/manage-agents");
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      let isOutside = true;
      // ✅ Check if the click happened inside any dropdown
      Object.values(dropdownRefs.current).forEach((ref) => {
        if (ref && ref.contains(event.target)) {
          isOutside = false;
        }
      });
      if (isOutside) {
        // ✅ Close the dropdown when clicking outside
        setOpenDropdown(null);
      }
    };
    // ✅ Use "mousedown" instead of "click" for better responsiveness
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleDropdownToggle = (rowId, event) => {
    event.stopPropagation();
    setOpenDropdown((prev) => (prev === rowId ? null : rowId));
  };
  const [rowData, setRowData] = useState([]);
  useEffect(() => {
    const fetchAgents = async () => {
      try {
        const res = await api.post("/list_agent");

        if (res.data.data && Array.isArray(res.data.data)) {
          setRowData(res.data.data);
        } else {
          console.warn("Unexpected data format:", res.data.data);
        }
      } catch (error) {
        console.error("Failed to fetch agents:", error);
      }
    };

    fetchAgents();
  }, []);

  // ✅ Handle row selection
  const handleRowSelect = (rowId) => {
    if (selectedRows.includes(rowId)) {
      setSelectedRows(selectedRows.filter((id) => id !== rowId));
    } else {
      setSelectedRows([...selectedRows, rowId]);
    }
  };

  // ✅ Handle "Select All"
  const handleSelectAll = () => {
    const pageRowIds = paginatedData.map((row) => row.Code);
    const allSelected = pageRowIds.every((id) => selectedRows.includes(id));
    if (allSelected) {
      setSelectedRows((prev) => prev.filter((id) => !pageRowIds.includes(id)));
    } else {
      setSelectedRows((prev) => [...new Set([...prev, ...pageRowIds])]);
    }
  };
  // ✅ Handle Sorting
  const handleSort = (field) => {
    if (sortBy === field) {
      // toggle order if clicking same column
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortOrder("asc"); 
    }
  };

  // ✅ Handle Search
  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };

  // ✅ Filtered and sorted data
  const filteredData = rowData
    .filter((row) =>
      Object.values(row).some(
        (value) =>
          value &&
          value.toString().toLowerCase().includes(searchQuery.toLowerCase())
      )
    )
    .sort((a, b) => {
      let aVal = a[sortBy] ?? "";
      let bVal = b[sortBy] ?? "";

      // For date field, parse date
      if (sortBy === "Create_Date_Time_Stamp") {
        aVal = new Date(aVal);
        bVal = new Date(bVal);
      }

      // For phone number, treat as string or number (if numeric strings)
      if (sortBy === "Phone_No") {
        // remove non-digits and convert to number for accurate comparison
        aVal = Number(aVal.toString().replace(/\D/g, "")) || 0;
        bVal = Number(bVal.toString().replace(/\D/g, "")) || 0;
      }

      if (typeof aVal === "string" && typeof bVal === "string") {
        if (sortOrder === "asc") {
          return aVal.localeCompare(bVal);
        } else {
          return bVal.localeCompare(aVal);
        }
      } else if (aVal instanceof Date && bVal instanceof Date) {
        if (sortOrder === "asc") {
          return aVal - bVal;
        } else {
          return bVal - aVal;
        }
      } else if (typeof aVal === "number" && typeof bVal === "number") {
        if (sortOrder === "asc") {
          return aVal - bVal;
        } else {
          return bVal - aVal;
        }
      } else {
        // fallback
        return 0;
      }
    });

  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const paginatedData = filteredData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handlePageChange = (page) => setCurrentPage(page);
  const handleToggleStatus = async (agentCode, currentStatus) => {
    const updatedStatus = currentStatus === "Active" ? "Inactive" : "Active";

    const isConfirmed = await confirm(
      `Do you want to mark this agent as ${updatedStatus}?`
    );

    if (!isConfirmed) return;

    try {
      const response = await api.post("/update_agent_status", {
        Code: agentCode,
        Status: updatedStatus,
      });

      if (response.data.success) {
        setRowData((prevData) =>
          prevData.map((agent) =>
            agent.Code === agentCode
              ? { ...agent, Status: updatedStatus }
              : agent
          )
        );
        alert("success", `Status updated to ${updatedStatus}`);
      } else {
        alert("error", response.data.message || "Failed to update status");
      }
    } catch (error) {
      console.error("Error updating status:", error);
      alert("error", "Something went wrong while updating the status.");
    }
  };

  return (
    <div className="dashboard">
      <Header title subtitle="Manage Agent" searchbar />
      <div className="center-content">
        <div className="container">
          <div className="pagegap d-flex flex-column">
            <AnimatePresence mode="wait">
              <motion.div
                className="pagegap d-flex flex-column"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.4 }}
              >
                <h2 className="create-titles mb-0 d-flex align-items-center">
                  <DiamondsFour /> <span>Manage Agent</span>
                </h2>
                <div className="tabletopbar d-flex align-items-center gap-2 justify-content-between flex-column flex-md-row">
                  <div className="tablefilter d-flex align-items-center">
                    <Link to="/create-agents" className="btn btngradiant">
                      <IconPlus /> Add Agent
                    </Link>

                    <div
                      className={`selacteditems d-flex align-items-center ${
                        selectedRows.length > 0 ? "selectedvalue" : ""
                      }`}
                    >
                      <span>{selectedRows.length} Selected</span>
                    </div>
                  </div>
                  <div className="d-flex align-items-center gap-3">
                    <div className="tablesearch position-relative">
                      <input
                        className="form-control"
                        type="text"
                        placeholder="Search"
                        value={searchQuery}
                        onChange={handleSearch}
                      />
                      <span className="position-absolute">
                        <IconSearchTable />
                      </span>
                    </div>

                    <CustomDropdown
                      name="itemsPerPage"
                      options={numberOptions}
                      value={itemsPerPage.toString()}
                      onChange={(value) => {
                        const num = Number(value);
                        setItemsPerPage(num);
                        setCurrentPage(1);
                      }}
                      placeholder="Items Per Page"
                      className="items-per-page-dropdown"
                    />
                  </div>
                </div>
                <div className="tablespace">
                  <div className="table-responsive">
                    <table className="table table-hover my-0">
                      <thead className="thead-light">
                        <tr>
                          <th width="150">
                            <div className="d-flex align-items-center gap-1">
                              <input
                                type="checkbox"
                                className="custom-control-input"
                                id="selectAllCheckbox"
                                ref={selectAllRef}
                                onChange={handleSelectAll}
                                checked={
                                  paginatedData.length > 0 &&
                                  paginatedData.every((row) =>
                                    selectedRows.includes(row.Code)
                                  )
                                }
                              />

                              <label
                                className="tablecheckbox headercheckbox position-relative"
                                htmlFor="selectAllCheckbox"
                              >
                                &nbsp;
                              </label>

                              <div className="d-flex align-items-center justify-content-between w-100">
                                <span>Name</span>

                                <span
                                  style={{ cursor: "pointer" }}
                                  onClick={() => handleSort("Agent_Name")}
                                >
                                  <IconFilter />
                                </span>
                              </div>
                            </div>
                          </th>
                          <th width="150">
                            <div className="d-flex align-items-center justify-content-between">
                              Agents Company
                              <span
                                style={{ cursor: "pointer" }}
                                onClick={() => handleSort("Agents_Company")}
                              >
                                <IconFilter />
                              </span>
                            </div>
                          </th>
                          {/* <th>E-Mail</th> */}
                          <th width="150">
                            <div className="d-flex align-items-center justify-content-between">
                              E-Mail
                              <span
                                style={{ cursor: "pointer" }}
                                onClick={() => handleSort("Email_Id")}
                              >
                                <IconFilter />
                              </span>
                            </div>
                          </th>
                          <th width="150">
                            <div className="d-flex align-items-center justify-content-between">
                              Mobile Numbe
                              <span
                                style={{ cursor: "pointer" }}
                                onClick={() => handleSort("Phone_No")}
                              >
                                <IconFilter />
                              </span>
                            </div>
                          </th>
                          {/* <th>User Type</th> */}

                          {/* <th>Password <span style={{ cursor: "pointer", marginLeft: "3px" }} onClick={handleToggleAllPasswords}> <IconKey /></span></th> */}

                          <th width="150">
                            <div className="d-flex align-items-center justify-content-between">
                              <span>
                                <IconDate /> Date & Time
                              </span>
                              <span
                                style={{ cursor: "pointer" }}
                                onClick={() =>
                                  handleSort("Create_Date_Time_Stamp")
                                }
                              >
                                <IconFilter />
                              </span>
                            </div>
                          </th>
                          {/* <th>
                            <IconDate /> Date & Time
                          </th> */}

                          <th width="70">
                            <div className="d-flex align-items-center justify-content-between">
                              Status
                              <span
                                style={{ cursor: "pointer" }}
                                onClick={() => handleSort("Status")}
                              >
                                <IconFilter />
                              </span>
                            </div>
                          </th>

                          <th width="50" className="text-end">
                            Action
                          </th>

                          <th width="1" style={{ padding: 0 }}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedData.length > 0 ? (
                          paginatedData.map((row, index) => (
                            <tr
                              key={row.Code}
                              className={
                                selectedRows.includes(row.Code)
                                  ? "selected-row"
                                  : ""
                              }
                            >
                              {/* Row Checkbox */}
                              <td>
                                <figure
                                  className="d-flex align-items-center custom-control-label"
                                  style={{ cursor: "pointer" }}
                                >
                                  <input
                                    type="checkbox"
                                    className="custom-control-input"
                                    id={`customCheckDisabled-${row.Agent_Name}`}
                                    checked={selectedRows.includes(row.Code)}
                                    onChange={() => handleRowSelect(row.Code)}
                                  />
                                  <label
                                    className="tablecheckbox customerstable position-relative d-flex align-items-center gap-2"
                                    htmlFor={`customCheckDisabled-${row.Agent_Name}`}
                                  >
                                    {/* {row.Images ? (
                                      <img
                                        className="rounded-circle m-0"
                                        width="20"
                                        height="20"
                                        src={`${IMAGE_BASE_URL}/upload/${row.Images}`}                                        
                                        alt={row.Agent_Name}
                                      />
                                    ) : (
                                      <Avatar
                                        name={row.Agent_Name}
                                        index={index}
                                        width={20}
                                        height={20}
                                        fontSize={10}
                                      />
                                    )} */}
                                    {row.Agent_Name}
                                  </label>
                                </figure>
                              </td>
                              <td>
                                <span>{row.Agents_Company}</span>
                              </td>

                              <td>
                                <span>{row.Email_Id}</span>
                              </td>
                              {/* <td><span>{row.type}</span></td> */}
                              <td>{row.Phone_No}</td>
                              {/* <td>{showAllPasswords[row.id] ? row.password : '******'}</td> */}
                              <td>
                                {selectedRows.includes(row.Code) ? (
                                  <IconDateActive />
                                ) : (
                                  <IconDate />
                                )}{" "}
                                {formatDate(row.Create_Date_Time_Stamp)}
                              </td>
                              <td>
                                {currentUser?.Code !== row.Code ? (
                                  <Link
                                    to="#"
                                    onClick={() =>
                                      handleToggleStatus(row.Code, row.Status)
                                    }
                                    className={
                                      row.Status === "Active"
                                        ? "statusbtn active"
                                        : "statusbtn inactive"
                                    }
                                  >
                                    {row.Status}
                                  </Link>
                                ) : (
                                  <span
                                    className={`statusbtn ${
                                      row.Status === "Active"
                                        ? "active"
                                        : "inactive"
                                    } disabled`}
                                    title="You can't change your own status"
                                    style={{
                                      cursor: "not-allowed",
                                      opacity: 0.6,
                                    }}
                                  >
                                    {row.Status}
                                  </span>
                                )}
                              </td>

                              <td align="right">
                                <div
                                  className={`actions-wrapper ${
                                    openDropdown === row.Code ? "open" : ""
                                  }`}
                                  ref={(el) =>
                                    (dropdownRefs.current[row.Code] = el)
                                  }
                                >
                                  <div
                                    className="tranparenteffect position-relative"
                                    onClick={(e) =>
                                      handleDropdownToggle(row.Code, e)
                                    }
                                  >
                                    <IconDots />
                                  </div>
                                  <div className="action-options">
                                    <Link
                                      to="/edit-agents-details"
                                      state={{ agent: row }}
                                    >
                                      <IconEdit /> Edit
                                    </Link>
                                    <Link
                                      to={`/view-agents-details/${row.Code}`}
                                      state={{ agent: row }}
                                    >
                                      <IconView /> View
                                    </Link>
                                    {/* <Link to="#" onClick={handleOptionClick}><IconDelete/> Delete</Link> */}
                                  </div>
                                </div>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="8" className="text-center">
                              No data found
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                  {totalPages > 1 && (
                    <Pagination
                      totalPages={totalPages}
                      currentPage={currentPage}
                      handlePageChange={handlePageChange}
                    />
                  )}
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </div>
  );
}
